/**
 * Checks if a URL is a YouTube Shorts video.
 * Must be called BEFORE converting to embed format, as embed URLs lose the /shorts/ path.
 */
export function isYouTubeShorts(url: string): boolean {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    if (!hostname.includes("youtube.com")) {
      return false;
    }

    // Check if the pathname contains /shorts/
    return urlObj.pathname.startsWith("/shorts/");
  } catch {
    // Invalid URL
    return false;
  }
}

/**
 * Converts YouTube URLs to embed format if needed.
 * Converts watch URLs and short URLs to embed URLs, but leaves embed URLs as-is.
 */
export function toEmbeddableYouTubeUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // Check if it's a YouTube domain
    if (!hostname.includes("youtube.com") && !hostname.includes("youtu.be")) {
      return url; // Not YouTube, return as-is
    }

    // If already an embed URL, return as-is
    if (urlObj.pathname.startsWith("/embed/")) {
      return url;
    }

    // Extract video ID from different YouTube URL formats
    let videoId: string | null = null;

    // youtube.com/watch?v=VIDEO_ID
    if (urlObj.pathname === "/watch") {
      videoId = urlObj.searchParams.get("v");
    }
    // youtu.be/VIDEO_ID (short URL)
    else if (hostname.includes("youtu.be")) {
      videoId = urlObj.pathname.slice(1); // Remove leading slash
    }
    // youtube.com/v/VIDEO_ID (older format)
    else if (urlObj.pathname.startsWith("/v/")) {
      videoId = urlObj.pathname.slice(3); // Remove "/v/"
    }
    // youtube.com/shorts/VIDEO_ID (YouTube Shorts)
    else if (urlObj.pathname.startsWith("/shorts/")) {
      videoId = urlObj.pathname.slice(8); // Remove "/shorts/"
    }

    // If we found a video ID, convert to embed URL
    if (videoId) {
      // Remove any query params from video ID
      const cleanVideoId = videoId.split("&")[0].split("?")[0];

      // Build embed URL and preserve timestamp parameter
      const embedUrl = new URL(`https://www.youtube.com/embed/${cleanVideoId}`);

      // Convert 't' parameter to 'start' for embed URLs
      const startTime =
        urlObj.searchParams.get("t") || urlObj.searchParams.get("start");
      if (startTime) {
        // Remove 's' suffix if present (e.g., "30s" -> "30")
        const seconds = startTime.replace(/s$/i, "");
        embedUrl.searchParams.set("start", seconds);
      }

      return embedUrl.toString();
    }

    // Couldn't extract video ID, return original URL
    return url;
  } catch {
    // Invalid URL, return as-is
    return url;
  }
}

/**
 * Converts Vimeo URLs to embed format if needed.
 * Converts regular Vimeo URLs to player.vimeo.com embed URLs.
 * Example: https://vimeo.com/123456789 → https://player.vimeo.com/video/123456789
 */
export function toEmbeddableVimeoUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // Check if it's a Vimeo domain
    if (!hostname.includes("vimeo.com")) {
      return url; // Not Vimeo, return as-is
    }

    // If already a player embed URL, return as-is
    if (hostname.includes("player.vimeo.com")) {
      return url;
    }

    // Extract video ID from vimeo.com/VIDEO_ID
    // Match pattern: /123456789 or /123456789/hash
    const pathMatch = urlObj.pathname.match(/^\/(\d+)/);

    if (pathMatch && pathMatch[1]) {
      const videoId = pathMatch[1];
      // Preserve the hash parameter if it exists (for unlisted videos)
      const hashParam = urlObj.searchParams.get("h");
      const hashQuery = hashParam ? `?h=${hashParam}` : "";
      // Preserve the hash fragment for timestamps (e.g., #t=1m30s)
      const hashFragment = urlObj.hash || "";
      return `https://player.vimeo.com/video/${videoId}${hashQuery}${hashFragment}`;
    }

    // Couldn't extract video ID, return original URL
    return url;
  } catch {
    // Invalid URL, return as-is
    return url;
  }
}

/**
 * Converts Airtable URLs to embed format if needed.
 * Converts regular Airtable shared view URLs to embeddable format by inserting /embed/.
 * Example: https://airtable.com/appJ2ui3PDBNTjz4P/shr9KjLinNIOlquHC
 *       → https://airtable.com/embed/appJ2ui3PDBNTjz4P/shr9KjLinNIOlquHC
 */
export function toEmbeddableAirtableUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // Check if it's an Airtable domain
    if (!hostname.includes("airtable.com")) {
      return url; // Not Airtable, return as-is
    }

    // If already an embed URL, return as-is
    if (urlObj.pathname.includes("/embed/")) {
      return url;
    }

    // Insert /embed/ before /app{id}
    // Pattern: /app{14 chars}/shr{14 chars}
    const newPathname = urlObj.pathname.replace(/^\/app/, "/embed/app");

    urlObj.pathname = newPathname;
    return urlObj.toString();
  } catch {
    // Invalid URL, return as-is
    return url;
  }
}

/**
 * Converts Notion URLs to embed format if needed.
 * Converts notion.so URLs to notion.site format and adds /ebd/ prefix.
 * Examples:
 *   https://www.notion.so/cats/185e717ec10a42fb9165beab1e82db59?v=...&source=copy_link
 *   → https://cats.notion.site/ebd/185e717ec10a42fb9165beab1e82db59?v=...
 *
 *   https://cats.notion.site/185e717ec10a42fb9165beab1e82db59?v=...
 *   → https://cats.notion.site/ebd/185e717ec10a42fb9165beab1e82db59?v=...
 */
export function toEmbeddableNotionUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // Check if it's a Notion domain
    if (!hostname.includes("notion.so") && !hostname.includes("notion.site")) {
      return url; // Not Notion, return as-is
    }

    // Handle notion.so URLs - convert to notion.site format
    if (hostname.includes("notion.so")) {
      // Extract workspace from path
      const pathParts = urlObj.pathname.split("/").filter((p) => p);

      // Skip /ebd/ if it's already in the path
      let startIndex = 0;
      if (pathParts[0] === "ebd") {
        startIndex = 1;
      }

      if (pathParts.length >= startIndex + 2) {
        const workspace = pathParts[startIndex];
        const restOfPath = pathParts.slice(startIndex + 1).join("/");

        // Convert to notion.site format
        urlObj.hostname = `${workspace}.notion.site`;
        urlObj.pathname = `/ebd/${restOfPath}`;
        urlObj.searchParams.delete("source");
        return urlObj.toString();
      }
    }

    // Handle notion.site URLs - just add /ebd/ if needed
    if (hostname.includes("notion.site")) {
      // If already has /ebd/ in path, just remove source param
      if (urlObj.pathname.includes("/ebd/")) {
        urlObj.searchParams.delete("source");
        return urlObj.toString();
      }

      // Add /ebd/ at start of path
      if (urlObj.pathname.startsWith("/")) {
        urlObj.pathname = "/ebd" + urlObj.pathname;
      }

      urlObj.searchParams.delete("source");
      return urlObj.toString();
    }

    return url;
  } catch {
    // Invalid URL, return as-is
    return url;
  }
}

/**
 * Converts Figma URLs to embed format if needed.
 * Converts regular figma.com URLs to embed.figma.com format.
 * Example: https://www.figma.com/board/hYorw1ogXm9KUGGUnnU37A/Untitled?node-id=2-2&t=SB5isq3hNz4XMvq7-1
 *       → https://embed.figma.com/board/hYorw1ogXm9KUGGUnnU37A/Untitled?node-id=2-2&embed-host=share
 */
export function toEmbeddableFigmaUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // Check if it's a Figma domain
    if (!hostname.includes("figma.com")) {
      return url; // Not Figma, return as-is
    }

    // If already an embed URL, return as-is
    if (hostname.includes("embed.figma.com")) {
      return url;
    }

    // Convert to embed.figma.com
    urlObj.hostname = "embed.figma.com";

    // Remove the 't' parameter (timestamp/tracking parameter)
    urlObj.searchParams.delete("t");

    // Add embed-host parameter if not already present
    if (!urlObj.searchParams.has("embed-host")) {
      urlObj.searchParams.set("embed-host", "share");
    }

    return urlObj.toString();
  } catch {
    // Invalid URL, return as-is
    return url;
  }
}

/**
 * Converts Loom URLs to embed format if needed.
 * Handles both share URLs and embed URLs.
 * Example: https://www.loom.com/share/{id} → https://www.loom.com/embed/{id}
 */
export function toEmbeddableLoomUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // Check if it's a Loom domain
    if (!hostname.includes("loom.com")) {
      return url; // Not Loom, return as-is
    }

    // If already an embed URL, return as-is
    if (urlObj.pathname.includes("/embed/")) {
      return url;
    }

    // Extract video ID from /share/{id} or /embed/{id}
    const pathMatch = urlObj.pathname.match(/\/(share|embed)\/([a-zA-Z0-9]+)/);
    if (pathMatch && pathMatch[2]) {
      const videoId = pathMatch[2];
      // Build embed URL and preserve query parameters (including timestamp)
      const embedUrl = new URL(`https://www.loom.com/embed/${videoId}`);
      // Copy all query parameters from original URL
      urlObj.searchParams.forEach((value, key) => {
        embedUrl.searchParams.set(key, value);
      });
      return embedUrl.toString();
    }

    // Couldn't extract video ID, return original URL
    return url;
  } catch {
    // Invalid URL, return as-is
    return url;
  }
}

/**
 * Converts URLs to their embeddable formats for various providers.
 */
export function toEmbeddableUrl(url: string): string {
  let normalized = toEmbeddableYouTubeUrl(url);
  normalized = toEmbeddableVimeoUrl(normalized);
  normalized = toEmbeddableLoomUrl(normalized);
  normalized = toEmbeddableAirtableUrl(normalized);
  normalized = toEmbeddableNotionUrl(normalized);
  normalized = toEmbeddableFigmaUrl(normalized);
  return normalized;
}
